/**
 * CiaoInput — Chat input bar with file attachment support
 * Clean, borderless, modern
 */

import React, { useState, useRef, useEffect } from 'react';
import { IoSend, IoAttach, IoClose, IoDocument, IoImage } from 'react-icons/io5';

const MAX_FILES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

interface CiaoInputProps {
  onSend: (message: string, files?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface FilePreview {
  file: File;
  preview: string | null; // data URI for images, null for PDFs
}

const CiaoInput: React.FC<CiaoInputProps> = ({ onSend, disabled = false, placeholder }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<FilePreview[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 100) + 'px';
    }
  }, [text]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((fp) => {
        if (fp.preview) URL.revokeObjectURL(fp.preview);
      });
    };
  }, [files]);

  const addFiles = (newFiles: FileList | File[]) => {
    const toAdd = Array.from(newFiles);
    const available = MAX_FILES - files.length;
    if (available <= 0) return;

    const valid = toAdd.slice(0, available).filter((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) return false;
      if (f.size > MAX_FILE_SIZE) return false;
      return true;
    });

    const previews: FilePreview[] = valid.map((f) => ({
      file: f,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));

    setFiles((prev) => [...prev, ...previews]);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0) || disabled) return;
    onSend(trimmed || '(adjunto)', files.length > 0 ? files.map((fp) => fp.file) : undefined);
    setText('');
    setFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const pastedFiles = items
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean) as File[];
    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFiles(pastedFiles);
    }
  };

  const canSend = !disabled && (text.trim() || files.length > 0);

  return (
    <div style={{ borderTop: '1px solid rgba(128,128,128,0.15)', background: 'var(--background-color, #fff)' }}>
      {/* File previews */}
      {files.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: '8px 12px 0',
            flexWrap: 'wrap',
          }}
        >
          {files.map((fp, idx) => (
            <div
              key={idx}
              style={{
                position: 'relative',
                borderRadius: 8,
                overflow: 'hidden',
                background: 'rgba(128,128,128,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: fp.preview ? 0 : '4px 8px',
                maxWidth: 140,
              }}
            >
              {fp.preview ? (
                <img
                  src={fp.preview}
                  alt={fp.file.name}
                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <>
                  <IoDocument size={14} style={{ color: '#FC7556', flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-color, #333)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 90,
                    }}
                  >
                    {fp.file.name}
                  </span>
                </>
              )}
              <button
                onClick={() => removeFile(idx)}
                style={{
                  position: fp.preview ? 'absolute' : 'relative',
                  top: fp.preview ? 2 : 'auto',
                  right: fp.preview ? 2 : 'auto',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                <IoClose size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 6,
          padding: '10px 12px 12px',
        }}
      >
        {/* Attach button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || files.length >= MAX_FILES}
          title={files.length >= MAX_FILES ? `Máximo ${MAX_FILES} archivos` : 'Adjuntar imagen o PDF'}
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'transparent',
            color: 'var(--text-color, #555)',
            opacity: disabled || files.length >= MAX_FILES ? 0.25 : 0.6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || files.length >= MAX_FILES ? 'default' : 'pointer',
            transition: 'color 0.15s, opacity 0.15s',
          }}
        >
          <IoAttach size={18} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = ''; // Reset so same file can be re-selected
          }}
        />

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
          placeholder={placeholder || 'Pregúntale algo a Ciao...'}
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: 'var(--button-color, #f5f5f5)',
            color: 'var(--text-color, #333)',
            borderRadius: 12,
            padding: '10px 14px',
            fontSize: 13,
            lineHeight: 1.4,
            maxHeight: 100,
            fontFamily: 'inherit',
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'text',
            transition: 'background 0.15s',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 10,
            background: canSend ? '#FC7556' : 'rgba(128,128,128,0.15)',
            color: canSend ? '#fff' : 'var(--text-color, #555)',
            opacity: canSend ? 1 : 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canSend ? 'pointer' : 'default',
            transition: 'all 0.15s ease',
          }}
        >
          <IoSend size={15} />
        </button>
      </div>
    </div>
  );
};

export default CiaoInput;
